import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ConfirmarModal from '../components/ConfirmarModal';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Usuários: cadastro dos operadores do Fiscal (nome + e-mail + login no Saber).
// Agenda Prazos Fiscais e e-mails do Fiscal passam só pela conta central (escolhida em
// Configurações, 19/10/2026): a conexão com o Google fica no card dela, não nas linhas.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const VAZIO = { nome: '', email: '', user_id: '', observacao: '', agenda_url: '' };

export default function FiscalUsuarios() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState(null);             // novo/edição de operador
  const [salvando, setSalvando] = useState(false);
  const [conectando, setConectando] = useState(null);  // id do operador conectando
  const [desconectar, setDesconectar] = useState(null);
  const [verInativos, setVerInativos] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/fiscal/usuarios/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar os operadores'))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  // a janela do Google avisa quando terminou (postMessage do callback)
  useEffect(() => {
    const ouvir = (ev) => {
      if (ev.data === 'fiscal-usuario-ok') { setConectando(null); setMsg('Conta Google conectada'); carregar(); }
      if (ev.data === 'fiscal-oauth-erro') { setConectando(null); }
    };
    window.addEventListener('message', ouvir);
    return () => window.removeEventListener('message', ouvir);
  }, [carregar]);

  const aplicar = (r) => { setDados(r.data); if (r.data.mensagem) setMsg(r.data.mensagem); };

  const salvar = () => {
    setSalvando(true); setErro('');
    const corpo = { nome: form.nome, email: form.email, user_id: form.user_id || null, observacao: form.observacao, agenda_url: form.agenda_url };
    const req = form.id
      ? axios.patch(`${API}/api/fiscal/usuarios/${form.id}/`, corpo, { headers: headers() })
      : axios.post(`${API}/api/fiscal/usuarios/`, corpo, { headers: headers() });
    req.then((r) => { aplicar(r); setForm(null); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar'))
      .finally(() => setSalvando(false));
  };

  const alternarAtivo = (o) => {
    axios.patch(`${API}/api/fiscal/usuarios/${o.id}/`, { ativo: !o.ativo }, { headers: headers() })
      .then(aplicar)
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível alterar'));
  };

  const conectar = (o) => {
    setConectando(o.id); setErro('');
    axios.get(`${API}/api/fiscal/usuarios/${o.id}/conectar/`, { headers: headers() })
      .then((r) => {
        const janela = window.open(r.data.authorization_url, 'fiscal-oauth-operador', 'width=520,height=680');
        if (!janela) { setErro('O navegador bloqueou a janela do Google — libere pop-ups para o Saber'); setConectando(null); return; }
        const vigia = setInterval(() => {
          if (janela.closed) { clearInterval(vigia); setConectando(null); carregar(); }
        }, 800);
      })
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível iniciar a conexão'); setConectando(null); });
  };

  const confirmarDesconexao = () => {
    setSalvando(true);
    axios.post(`${API}/api/fiscal/usuarios/${desconectar.id}/desconectar/`, {}, { headers: headers() })
      .then((r) => { aplicar(r); setDesconectar(null); })
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível desconectar'); setDesconectar(null); })
      .finally(() => setSalvando(false));
  };

  const central = dados?.central;
  const podeGerir = !!dados?.pode_gerir;
  const lista = (dados?.operadores || []).filter((o) => verInativos || o.ativo);
  const opCentral = (dados?.operadores || []).find((o) => o.id === central?.operador_id);

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-users-gear header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Usuários do Fiscal</h2>
          </div>
          <div className="gr-toolbar">
            <label className="gr-campo" title="Mostrar também os inativos">
              <input type="checkbox" checked={verInativos} onChange={(e) => setVerInativos(e.target.checked)} style={{ marginRight: 6 }} />ver inativos
            </label>
            <button type="button" className="gr-btn" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
            {podeGerir && (
              <button type="button" className="gr-btn primaria" onClick={() => setForm({ ...VAZIO })}>
                <i className="fas fa-user-plus" aria-hidden="true"></i>Novo operador
              </button>
            )}
          </div>
        </div>

        <div className="alert alert-light border small py-2">
          <i className="fas fa-circle-info me-2 text-primary"></i>
          Cadastre quem opera o Fiscal e vincule o <b>login no Saber</b> (usado em "Só as minhas", no Painel do Operador e nos responsáveis das empresas).
          A agenda <b>Prazos Fiscais</b> e os e-mails como <b>{central?.esperado || 'fiscal@'}</b> passam só pela <b>conta central</b>, escolhida em Configurações
          e conectada ao Google no card abaixo (o Saber não lê a caixa de entrada).
          {!podeGerir && <> Cadastro e alterações de operadores são feitos pela gestão.</>}
        </div>
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}

        <div className="po-kpis" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)' }}>
          <div className="po-kpi c-navy" style={{ cursor: 'default' }}>
            <div className="po-kpi-rotulo"><i className="fas fa-users"></i>Operadores ativos</div>
            <div className="po-kpi-valor">{(dados?.operadores || []).filter((o) => o.ativo).length}</div>
          </div>
          <div className={`po-kpi ${central?.email ? 'c-ok' : 'c-warn'}`} style={{ cursor: 'default' }}>
            <div className="po-kpi-rotulo"><i className="fas fa-envelope"></i>Conta central</div>
            <div className="po-kpi-sub mt-2"><b>{central?.operador_nome || 'não escolhida'}</b> · como {central?.esperado || 'fiscal@'}</div>
            <div className="po-kpi-sub">{central?.email ? `conectada (${central.conta})${central.autenticado_em ? ` desde ${central.autenticado_em}` : ''}` : 'não conectada'} · <Link to="/Fiscal/Configuracoes">trocar em Configurações</Link></div>
            {opCentral && (
              <div className="mt-2">
                <button className="btn btn-sm btn-primary me-1" onClick={() => conectar(opCentral)} disabled={conectando === opCentral.id || !opCentral.email}
                  title={!opCentral.email ? 'Cadastre o e-mail Google do operador antes de conectar' : `O Google abre com ${opCentral.email}; só essa conta é aceita`}>
                  <i className={`fab fa-google ${conectando === opCentral.id ? 'fa-beat' : ''} me-1`}></i>{opCentral.google ? 'Reconectar' : 'Conectar conta Google'}
                </button>
                {opCentral.google && (podeGerir || opCentral.user_id === dados.meu_user_id) && (
                  <button className="btn btn-sm btn-outline-secondary" title="Desconectar a conta Google" onClick={() => setDesconectar(opCentral)}><i className="fas fa-link-slash"></i></button>
                )}
              </div>
            )}
          </div>
        </div>

        <section className="po-card po-enter">
          <header className="po-card-head">
            <h3><i className="fas fa-users" aria-hidden="true"></i>Operadores</h3>
            <span className="badge bg-light text-dark">{lista.length}</span>
          </header>
          <div className="po-card-body">
            <div>
              <table className="table table-hover po-table align-middle mb-0">
                <thead><tr><th>Operador</th><th>Login no Saber</th><th className="text-center">Empresas</th><th>Situação</th><th></th></tr></thead>
                <tbody>
                  {lista.map((o) => {
                    const souEu = o.user_id && o.user_id === dados.meu_user_id;
                    return (
                      <tr key={o.id} style={o.ativo ? undefined : { opacity: .55 }}>
                        <td>
                          <div className="fw-semibold">{o.nome}{souEu && <span className="badge bg-info text-dark ms-2">você</span>}</div>
                          <div className="fz-sub">{o.email || <span className="text-muted">sem e-mail</span>}{o.id === central?.operador_id && <span className="badge bg-light text-dark border ms-2">conta central</span>}</div>
                          {o.agenda_url && <div className="fz-sub"><i className="fas fa-calendar-plus me-1"></i>agenda cadastrada</div>}
                        </td>
                        <td className="small">{o.login || <span className="text-muted">sem login</span>}{o.contador && <div className="fz-sub"><i className="fas fa-user-tie me-1"></i>contador</div>}</td>
                        <td className="text-center fz-num">{o.user_id ? o.empresas : '—'}</td>
                        <td>{o.ativo ? <span className="badge bg-success">ativo</span> : <span className="badge bg-secondary">inativo</span>}</td>
                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                          {podeGerir && (
                            <>
                              <button className="btn btn-sm btn-outline-primary me-1" title="Editar"
                                onClick={() => setForm({ id: o.id, nome: o.nome, email: o.email || '', user_id: o.user_id || '', observacao: o.observacao || '', agenda_url: o.agenda_url || '' })}>
                                <i className="fas fa-pen"></i>
                              </button>
                              <button className={`btn btn-sm ${o.ativo ? 'btn-outline-danger' : 'btn-outline-success'}`} title={o.ativo ? 'Inativar' : 'Reativar'} onClick={() => alternarAtivo(o)}>
                                <i className={`fas ${o.ativo ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {dados && lista.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">Nenhum operador cadastrado. Use "Novo operador".</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <div className="form-text mt-2">
          Se o Google mostrar erro de <i>redirect_uri</i>, o endereço <span className="fz-mono">{dados?.redirect_uri}</span> precisa estar cadastrado no OAuth Client do Google Cloud.
        </div>

        {form && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className={`fas ${form.id ? 'fa-pen' : 'fa-user-plus'} me-2`} style={{ color: '#00b5cc' }}></i>{form.id ? 'Editar operador' : 'Novo operador'}
                    </h5>
                    <small style={{ color: '#6c757d' }}>{form.id ? form.nome : 'quem opera o Fiscal'}</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setForm(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Nome</label>
                      <input className="form-control" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} autoFocus />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">E-mail da conta Google <span className="text-muted fw-normal">(obrigatório se for a conta central)</span></label>
                      <input type="email" className="form-control" placeholder="nome@empresa.com.br" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                      <div className="form-text">Só esta conta é aceita ao conectar.{form.id && ' Trocar o e-mail desconecta a conta atual.'}</div>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Login no Saber <span className="text-muted fw-normal">(opcional)</span></label>
                      <select className="form-select" value={form.user_id || ''} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}>
                        <option value="">— sem login —</option>
                        {(dados?.usuarios || []).map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
                      </select>
                      <div className="form-text">Liga o operador às empresas de que ele é responsável e ao Painel do Operador.</div>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Observação <span className="text-muted fw-normal">(opcional)</span></label>
                      <input className="form-control" value={form.observacao} onChange={(e) => setForm((f) => ({ ...f, observacao: e.target.value }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Link de agenda <span className="text-muted fw-normal">(opcional)</span></label>
                      <input type="url" className="form-control" placeholder="https://calendar.app.google/..." value={form.agenda_url}
                        onChange={(e) => setForm((f) => ({ ...f, agenda_url: e.target.value }))} />
                      <div className="form-text">Vira o botão "Agendar reunião" no e-mail de fechamento fiscal das empresas deste operador.</div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary me-auto" onClick={() => setForm(null)}><i className="fas fa-times me-2"></i>Cancelar</button>
                  <button className="btn btn-primary" onClick={salvar} disabled={salvando || !form.nome.trim()}>
                    <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>{salvando ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmarModal aberto={!!desconectar} perigo titulo="Desconectar conta Google" rotuloConfirmar="Desconectar" processando={salvando}
          texto={desconectar && <>O Saber apaga a autorização da conta <b>{desconectar.google?.email}</b> de {desconectar.nome}. Se ela for a conta central, a agenda Prazos Fiscais e os e-mails do Fiscal param até reconectar.
            Para revogar também no Google, a pessoa usa a página "Segurança" da conta Google dela.</>}
          onConfirmar={confirmarDesconexao} onCancelar={() => setDesconectar(null)} />
      </div>
    </div>
  );
}
